"use client"

import * as React from "react"
import { type PaginationState, type Updater } from "@tanstack/react-table"
import { useLocation, useNavigate } from "react-router-dom"

type UseDataTableUrlPaginationOptions = {
  initialPagination: PaginationState
  enabled: boolean
  mode?: "replace" | "push"
  keepExplicitParams?: boolean
}

type UseDataTableUrlPaginationResult = {
  pagination: PaginationState
  setPagination: (updater: Updater<PaginationState>) => void
}

export function useDataTableUrlPagination({
  initialPagination,
  enabled,
  mode = "replace",
  keepExplicitParams = true,
}: UseDataTableUrlPaginationOptions): UseDataTableUrlPaginationResult {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search])

  const pageParamKey = "page"
  const sizeParamKey = "limit"

  const hasExplicitParamsRef = React.useRef(false)
  React.useEffect(() => {
    if (!enabled) return
    if (searchParams.has(pageParamKey) || searchParams.has(sizeParamKey)) {
      hasExplicitParamsRef.current = true
    }
  }, [enabled, searchParams])

  const pagination = React.useMemo<PaginationState>(() => {
    if (!enabled) {
      return {
        pageIndex: Math.max(0, Math.floor(initialPagination.pageIndex)),
        pageSize: Math.max(1, Math.floor(initialPagination.pageSize)),
      }
    }

    const pageValue = searchParams.get(pageParamKey)
    const sizeValue = searchParams.get(sizeParamKey)

    const parsedPage = parsePositiveInteger(pageValue)
    const parsedSize = parsePositiveInteger(sizeValue)

    const pageIndex =
      parsedPage !== null
        ? parsedPage - 1
        : Math.max(0, Math.floor(initialPagination.pageIndex))
    const pageSize =
      parsedSize !== null
        ? parsedSize
        : Math.max(1, Math.floor(initialPagination.pageSize))

    return { pageIndex, pageSize }
  }, [enabled, initialPagination.pageIndex, initialPagination.pageSize, searchParams])

  const paginationRef = React.useRef(pagination)
  React.useEffect(() => {
    paginationRef.current = pagination
  }, [pagination])

  const setPagination = React.useCallback(
    (updater: Updater<PaginationState>) => {
      if (!enabled) return

      const nextState =
        typeof updater === "function"
          ? updater(paginationRef.current)
          : updater

      const sanitizedNext: PaginationState = {
        pageIndex: Math.max(0, Math.floor(nextState.pageIndex)),
        pageSize: Math.max(1, Math.floor(nextState.pageSize)),
      }

      const normalizedDefaultPageIndex = Math.max(
        0,
        Math.floor(initialPagination.pageIndex)
      )
      const normalizedDefaultPageSize = Math.max(
        1,
        Math.floor(initialPagination.pageSize)
      )
      const preserveExplicitParams =
        keepExplicitParams &&
        (hasExplicitParamsRef.current ||
          searchParams.has(pageParamKey) ||
          searchParams.has(sizeParamKey))

      const nextParams = new URLSearchParams(searchParams.toString())

      if (!preserveExplicitParams && sanitizedNext.pageIndex === normalizedDefaultPageIndex) {
        nextParams.delete(pageParamKey)
      } else {
        nextParams.set(pageParamKey, String(sanitizedNext.pageIndex + 1))
      }

      if (!preserveExplicitParams && sanitizedNext.pageSize === normalizedDefaultPageSize) {
        nextParams.delete(sizeParamKey)
      } else {
        nextParams.set(sizeParamKey, String(sanitizedNext.pageSize))
      }

      const currentQuery = searchParams.toString()
      const nextQuery = nextParams.toString()
      if (currentQuery === nextQuery) return

      const href = nextQuery ? `${pathname}?${nextQuery}` : pathname

      if (mode === "push") {
        navigate(href, { replace: false, preventScrollReset: true })
        return
      }

      navigate(href, { replace: true, preventScrollReset: true })
    },
    [
      enabled,
      initialPagination.pageIndex,
      initialPagination.pageSize,
      keepExplicitParams,
      mode,
      pathname,
      navigate,
      searchParams,
    ]
  )

  return { pagination, setPagination }
}

function parsePositiveInteger(value: string | null): number | null {
  if (!value) return null
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 1) return null
  return parsed
}
